// Web Audio SFX — ported from wiiplaytanks (the user's own game). Real samples loaded from
// /sounds/manifest.json with a synth-beep fallback per sound. POSITIONAL one-shots (attenuated +
// low-passed + reverb-sent by distance from the listener) and continuous LOOPING voices via
// sustain() (tank treads / turret servo). Distances are in STUDS and a hard CUTOFF drops far
// events so a battle of hundreds of units doesn't stack into a wall of noise. (Background music is
// separate — see engine/music.ts, which streams tracks through an HTMLAudioElement.)
//
// Audio-unlock: browsers won't start an AudioContext until a user gesture, so we resume on the
// first interaction anywhere on the page.

export type SfxName =
  | "fire" // tank cannon (direct/rocket/sniper)
  | "artillery" // mortar (arc) lob
  | "bounce" // shell ricochet off a wall
  | "explode" // mortar land / kamikaze / tank destroyed
  | "melee" // fighter punch lands
  | "death" // fighter killed
  | "ouch" // giant killed (heavier)
  | "footstep" // giant footfall
  | "round" // new level generated
  | "drive" // LOOP: nearest tank's treads (via sustain)
  | "turretspin" // LOOP: nearest tank's turret servo (via sustain)
  | "jump" // player jump (from zombies)
  // ---- player weapons + shop (ported from zombies) ----
  | "gun" // player gun shot
  | "hit" // player shot connected
  | "kill" // player shot killed
  | "reload"
  | "swap" // weapon switch
  | "throw" // deploy/grenade lob
  | "deny" // out of ammo / can't afford
  | "buy" // purchase at a shop station
  | "upgrade" // pack-a-punch
  | "powerup" // (reserved) power-up pickup
  | "uiSelect"; // campaign: UI click — selecting a tile / planet / star

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

interface Tone {
  freq: number;
  type: OscillatorType;
  dur: number;
  gain: number;
  slide?: number;
}

// synth fallbacks (only used if the real sample fails to load)
const TONES: Partial<Record<SfxName, Tone>> = {
  fire: { freq: 660, type: "square", dur: 0.05, gain: 0.5 },
  artillery: { freq: 150, type: "sawtooth", dur: 0.22, gain: 0.8, slide: 120 },
  bounce: { freq: 760, type: "square", dur: 0.05, gain: 0.4, slide: -260 },
  explode: { freq: 140, type: "sawtooth", dur: 0.25, gain: 0.95, slide: -60 },
  melee: { freq: 520, type: "square", dur: 0.07, gain: 0.6, slide: -260 },
  death: { freq: 200, type: "sawtooth", dur: 0.12, gain: 0.6, slide: -90 },
  ouch: { freq: 160, type: "sawtooth", dur: 0.2, gain: 0.7, slide: -70 },
  footstep: { freq: 90, type: "sine", dur: 0.08, gain: 0.4, slide: -20 },
  round: { freq: 520, type: "sine", dur: 0.18, gain: 0.7, slide: 280 },
  jump: { freq: 320, type: "sine", dur: 0.12, gain: 0.5, slide: 220 },
};

const VOL: Partial<Record<SfxName, number>> = {
  fire: 0.4,
  artillery: 0.6,
  bounce: 0.34,
  explode: 0.62,
  melee: 0.34,
  death: 0.4,
  ouch: 0.5,
  footstep: 0.3,
  round: 0.6,
  jump: 0.4,
  gun: 0.5,
  hit: 0.5,
  kill: 0.55,
  reload: 0.5,
  swap: 0.5,
  throw: 0.5,
  deny: 0.5,
  buy: 0.6,
  upgrade: 0.7,
  powerup: 0.6,
  uiSelect: 0.5, // campaign UI click
  drive: 0.16, // loop
  turretspin: 0.12, // loop
};

// distance model (studs). longfight maps span ~10k studs, so these are much larger than wiiplaytanks'.
const DIST_FALL = 2600; // gain reaches its floor around here
const DIST_FLOOR = 0.25; // never fully silent within CUTOFF (battle stays audible from the orbit view)
const MUFFLE_START = 900; // highs roll off past this
const REVERB_START = 600; // reverb send grows past this
const CUTOFF = 5200; // one-shots beyond this are skipped entirely (density control)
const MASTER_MAX = 0.55;

// dense events get a tighter self-throttle so a mass battle doesn't machine-gun one sound
const THROTTLE: Partial<Record<SfxName, number>> = {
  fire: 0.05, bounce: 0.05, explode: 0.05, artillery: 0.06, melee: 0.06, death: 0.06, footstep: 0.05,
  gun: 0.03, hit: 0.04, kill: 0.05,
};

class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private reverb: ConvolverNode | null = null;
  muted = false;
  private volume = 0.5;
  private last: Partial<Record<SfxName, number>> = {};
  private unlockInstalled = false;
  private samples = new Map<string, AudioBuffer>();
  private manifestTried = false;
  private listener: Vec3 = { x: 0, y: 0, z: 0 };
  private sustains = new Map<string, { src: AudioBufferSourceNode; gain: GainNode }>();
  private streams = new Map<string, { el: HTMLAudioElement; dry: GainNode; wet?: GainNode; lp?: BiquadFilterNode }>();
  private subs = new Map<string, { out: GainNode }>();

  constructor() {
    const saved = parseFloat(localStorage.getItem("lfsfxvol") ?? "");
    if (!isNaN(saved)) this.volume = Math.max(0, Math.min(1, saved));
    const savedMute = localStorage.getItem("lfsfxmute");
    if (savedMute === "1") this.muted = true;
    this.installGestureUnlock();
  }

  setListener(x: number, y: number, z: number): void {
    this.listener.x = x;
    this.listener.y = y;
    this.listener.z = z;
  }

  /** The current listener position (read-only) — for callers picking the nearest sound source. */
  listenerPos(): Readonly<Vec3> {
    return this.listener;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.muted ? 0 : this.volume * MASTER_MAX;
    try {
      localStorage.setItem("lfsfxvol", String(this.volume));
    } catch {
      /* ignore */
    }
  }

  getVolume(): number {
    return this.volume;
  }

  resume(): void {
    if (!this.ctx) {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : this.volume * MASTER_MAX;
      this.master.connect(this.ctx.destination);
      const ret = this.ctx.createGain();
      ret.gain.value = 0.85;
      ret.connect(this.master);
      this.reverb = this.ctx.createConvolver();
      this.reverb.buffer = this.makeImpulse(2.2, 2.6);
      this.reverb.connect(ret);
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    this.loadManifest();
  }

  private makeImpulse(seconds: number, decay: number): AudioBuffer {
    const ctx = this.ctx!;
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
    return buf;
  }

  private loadManifest(): void {
    if (this.manifestTried || !this.ctx) return;
    this.manifestTried = true;
    fetch("/sounds/manifest.json")
      .then((r) => (r.ok ? r.json() : null))
      .then(async (m: Record<string, string> | null) => {
        if (!m || !this.ctx) return;
        for (const [name, url] of Object.entries(m)) {
          try {
            const data = await fetch(url).then((r) => r.arrayBuffer());
            this.samples.set(name, await this.ctx.decodeAudioData(data));
          } catch {
            /* skip a bad entry */
          }
        }
      })
      .catch(() => {});
  }

  private dist(pos?: Vec3): number {
    return pos ? Math.hypot(pos.x - this.listener.x, pos.y - this.listener.y, pos.z - this.listener.z) : 0;
  }

  private routeOut(dist: number, baseGain: number): AudioNode {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.value = baseGain * Math.max(DIST_FLOOR, 1 - dist / DIST_FALL);
    g.connect(this.master!);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = Math.max(1400, 18000 - Math.max(0, dist - MUFFLE_START) * 12);
    lp.connect(g);
    if (this.reverb && dist > REVERB_START) {
      const send = ctx.createGain();
      send.gain.value = Math.min(0.45, (dist - REVERB_START) / 2400);
      g.connect(send);
      send.connect(this.reverb);
    }
    return lp;
  }

  private playBuffer(buf: AudioBuffer, dist: number, baseGain: number): void {
    const src = this.ctx!.createBufferSource();
    src.buffer = buf;
    src.connect(this.routeOut(dist, baseGain));
    src.start();
  }

  /** Play a one-shot. Pass a world position for distance attenuation; far events are dropped. */
  play(type: SfxName, pos?: Vec3): void {
    if (this.muted || !this.ctx || !this.master) return;
    if (this.ctx.state !== "running") {
      void this.ctx.resume();
      return;
    }
    const dist = this.dist(pos);
    if (dist > CUTOFF) return; // too far to matter — skip (keeps a mass battle from stacking up)
    const now = this.ctx.currentTime;
    const th = THROTTLE[type];
    if (th && now - (this.last[type] ?? 0) < th) return;
    this.last[type] = now;

    const sample = this.samples.get(type);
    if (sample) {
      this.playBuffer(sample, dist, VOL[type] ?? 0.6);
      return;
    }
    const t = TONES[type];
    if (!t) return;
    const out = this.routeOut(dist, 1);
    const osc = this.ctx.createOscillator();
    const env = this.ctx.createGain();
    osc.type = t.type;
    osc.frequency.setValueAtTime(t.freq, now);
    if (t.slide) osc.frequency.linearRampToValueAtTime(Math.max(40, t.freq + t.slide), now + t.dur);
    env.gain.setValueAtTime(t.gain, now);
    env.gain.exponentialRampToValueAtTime(0.0001, now + t.dur);
    osc.connect(env).connect(out);
    osc.start(now);
    osc.stop(now + t.dur + 0.02);
  }

  /**
   * Drive a continuous LOOPING sample (nearest tank's treads / turret servo). Call EVERY frame:
   * on=true (with pos) while active, on=false to fade out. One persistent voice per name is built
   * lazily and gain-ramped (no restart click). No-op without a loaded sample.
   */
  sustain(name: SfxName, on: boolean, pos?: Vec3): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const want = on && !this.muted && ctx.state === "running";
    let voice = this.sustains.get(name);
    if (want && !voice) {
      const buf = this.samples.get(name);
      if (!buf) return;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0.0001;
      src.connect(gain).connect(this.master);
      src.start(now);
      voice = { src, gain };
      this.sustains.set(name, voice);
    }
    if (!voice || ctx.state !== "running") return;
    let target = 0.0001;
    if (want) target = (VOL[name] ?? 0.15) * Math.max(DIST_FLOOR, 1 - this.dist(pos) / DIST_FALL);
    voice.gain.gain.setTargetAtTime(target, now, 0.08);
  }

  /**
   * A long looping AMBIENCE (the campaign's space drone / planet chimes) STREAMED via an
   * HTMLAudioElement — no decodeAudioData, so a multi-minute bed costs ~no RAM — routed through the
   * Web Audio graph so master mute/volume + the shared reverb apply. Call EVERY frame: on=true fades
   * in, on=false fades out. opts.gain = dry level; opts.rate = playbackRate (<1 with pitch-preservation
   * OFF = slower + an octave deeper, the "tape slowdown"); opts.reverb = wet send 0..1; opts.fade =
   * ramp seconds. Built lazily on the first on=true (so unused beds cost nothing). No-op until unlocked.
   */
  ambientStream(
    name: string,
    url: string,
    on: boolean,
    opts: { gain?: number; rate?: number; reverb?: number; fade?: number;
            band?: [number, number]; seek?: boolean } = {},
  ): void {
    if (!this.ctx || !this.master || this.ctx.state !== "running") return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    let v = this.streams.get(name);
    let lpf: BiquadFilterNode | undefined;
    if (!v) {
      if (!on) return; // don't build the element until it's first needed
      const el = new Audio(url);
      el.loop = true;
      el.preload = "auto";
      try {
        (el as unknown as { preservesPitch: boolean }).preservesPitch = false; // rate<1 → deeper, not just slower
      } catch {
        /* not supported on older browsers */
      }
      el.playbackRate = opts.rate ?? 1;
      // SEEK: start somewhere random in a long capture. One file then plays differently every session, and a
      // recording too long to loop audibly stops having a "beginning" the player learns.
      if (opts.seek) {
        const jump = (): void => {
          try {
            if (isFinite(el.duration) && el.duration > 1) el.currentTime = Math.random() * el.duration * 0.98;
          } catch { /* seeking not ready / not seekable */ }
        };
        if (el.readyState >= 1) jump();
        else el.addEventListener("loadedmetadata", jump, { once: true });
      }
      let srcNode: MediaElementAudioSourceNode;
      try {
        srcNode = ctx.createMediaElementSource(el);
      } catch {
        return; // element already bound / cross-origin — bail
      }
      // BAND: a weak skip signal is far narrower than the FM band it arrived on — roughly 300 Hz to 4 kHz.
      // Squeezing the source into that window is most of what makes a recording read as "picked up" rather than
      // "played back", and it also keeps this layer clear of the sub bed's 22-150 Hz so neither masks the other.
      let tap: AudioNode = srcNode;
      if (opts.band) {
        const hp = ctx.createBiquadFilter();
        hp.type = "highpass"; hp.frequency.value = opts.band[0]; hp.Q.value = 0.7;
        lpf = ctx.createBiquadFilter();
        lpf.type = "lowpass"; lpf.frequency.value = opts.band[1]; lpf.Q.value = 0.7;
        srcNode.connect(hp).connect(lpf);
        tap = lpf;
      }
      const dry = ctx.createGain();
      dry.gain.value = 0.0001;
      tap.connect(dry).connect(this.master);
      let wet: GainNode | undefined;
      if (opts.reverb && this.reverb) {
        wet = ctx.createGain();
        wet.gain.value = 0.0001;
        tap.connect(wet).connect(this.reverb);
      }
      void el.play().catch(() => {});
      v = { el, dry, wet, lp: lpf };
      this.streams.set(name, v);
    }
    const fade = opts.fade ?? 0.6;
    v.dry.gain.setTargetAtTime(on ? opts.gain ?? 0.3 : 0.0001, now, fade);
    if (v.wet) v.wet.gain.setTargetAtTime(on ? opts.reverb ?? 0 : 0.0001, now, fade);
    // The band's upper edge is RETARGETABLE, not fixed at build time — opening it is what a signal resolving
    // sounds like, so a caller can widen it as the story earns it. Slow, so it is never heard moving.
    if (opts.band && v.lp) v.lp.frequency.setTargetAtTime(opts.band[1], now, 8);
  }

  /** A SYNTHESIZED sub-bass swell bed — the slow "waves on a beach" under the campaign map.
   *
   *  Synthesized rather than streamed, and not marginally: at 38 Hz the sound IS two or three oscillators, so a
   *  file buys nothing but bytes. More importantly the swells run 20-45s, and a loop long enough to hide its
   *  seam would be enormous — while the ear finds loop points mercilessly in a quiet bed. Three LFOs on
   *  mutually incommensurate periods (19 / 27.4 / 43.1s) never line up, so the bed genuinely never repeats.
   *  Cost is ~12 native nodes and no download.
   *
   *  Built for a subwoofer, which drives three decisions that matter:
   *   • MONO. Below ~100 Hz stereo phase differences cancel when a system sums to the sub, and subs are mono
   *     anyway. Every layer here is single-channel on purpose — do not "widen" it.
   *   • The fundamental stays CLEAN; only the octave-up layer is saturated. Distorting sub-bass generates
   *     intermodulation mud that a big driver reproduces all too faithfully. This is how bass is mixed.
   *   • NO reverb send. Sub through a 2.2s convolver is porridge.
   *  The 2× harmonic also means laptops and phones, which reproduce nothing at 38 Hz, still hear the swell —
   *  quieter, but present, rather than silence where the bed should be.
   *
   *  Idempotent like `ambientStream`: safe to call every frame. Oscillators cannot be restarted once stopped,
   *  so turning it "off" just fades to silence and leaves the graph parked (it costs nothing running).
   */
  ambientSub(
    name: string,
    on: boolean,
    opts: { gain?: number; freq?: number; fade?: number } = {},
  ): void {
    if (!this.ctx || !this.master || this.ctx.state !== "running") return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    let v = this.subs.get(name);
    if (!v) {
      if (!on) return;
      const f = opts.freq ?? 38;                 // low enough to read as FELT rather than pitched, so it can't
                                                 // fight the space drone's key; high enough that subs reproduce it
      const out = ctx.createGain();
      out.gain.value = 0.0001;

      // ── the swell: base + three summed LFOs. Base exceeds the summed depth, so the product never goes
      //    negative (which would flip polarity rather than mute).
      const swell = ctx.createGain();
      swell.gain.value = 0.58;
      const lfoSum = ctx.createGain();
      lfoSum.gain.value = 1;
      // Periods deliberately in DIFFERENT decades. Three LFOs of similar rate just interfere into a busy,
      // rippling crest (measured: spurious 6s peaks on top of the real swell). Surf is one clear wave, whose
      // size drifts over a much longer set cycle, with a little texture on top — so: 31s wave, 73s sets,
      // 13.7s texture. Base exceeds the summed depth so the product never inverts polarity, and the trough
      // floors at ~0.15 rather than 0 — waves recede, they don't switch the sound off.
      for (const [period, depth] of [[31, 0.22], [73, 0.14], [13.7, 0.07]]) {
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 1 / period;
        const d = ctx.createGain();
        d.gain.value = depth;
        lfo.connect(d).connect(lfoSum);
        lfo.start();
      }
      lfoSum.connect(swell.gain);

      // A swell that only gets LOUDER reads as a fader move. Opening the filter with it is what makes it read
      // as something arriving — the wave brightens as it comes in, exactly like surf.
      const tone = ctx.createBiquadFilter();
      tone.type = "lowpass";
      tone.frequency.value = 150;
      tone.Q.value = 0.7;
      const cutMod = ctx.createGain();
      cutMod.gain.value = 70;                    // ±70 Hz of cutoff travel, tracking the same swell
      lfoSum.connect(cutMod).connect(tone.frequency);

      // ── fundamental: two sines a hair apart, and the size of that hair is critical. Detuning by 0.4% beats
      //    at 0.15 Hz — a 6.6s wobble that reads as pulsing, not fuzz, and because two equal sines cancel
      //    COMPLETELY at the null it also punched the bed to near-silence every 6.6s (measured: it, not the
      //    LFOs, was setting the envelope). 0.05% beats once per ~53s instead, which is slow enough to melt
      //    into the swell as drift. The FUZZ comes from the saturated octave below, not from here.
      const subMix = ctx.createGain();
      subMix.gain.value = 0.5;
      for (const mul of [1, 1.0005]) {
        const o = ctx.createOscillator();
        o.type = "sine";
        o.frequency.value = f * mul;
        o.connect(subMix);
        o.start();
      }

      // ── octave up, saturated. This layer carries the grit AND the audibility on small speakers.
      const harm = ctx.createOscillator();
      harm.type = "triangle";
      harm.frequency.value = f * 2;
      const shaper = ctx.createWaveShaper();
      const curve = new Float32Array(1024);
      for (let i = 0; i < 1024; i++) {
        const x = (i / 1023) * 2 - 1;
        curve[i] = Math.tanh(x * 2.2);           // gentle asymmetric-free soft clip: adds odd harmonics, no fizz
      }
      shaper.curve = curve;
      shaper.oversample = "2x";
      const harmGain = ctx.createGain();   // 0.18, not lower: this layer is ALL a laptop speaker can reproduce
      harmGain.gain.value = 0.18;
      harm.connect(shaper).connect(harmGain).connect(subMix);
      harm.start();

      // ── a breath of brown noise. Brown (integrated white) falls at -6dB/oct, so it is already bass-weighted;
      //    lowpassed hard it reads as air moving rather than as hiss.
      const nb = ctx.createBuffer(1, ctx.sampleRate * 4, ctx.sampleRate);
      const nd = nb.getChannelData(0);
      let last = 0;
      for (let i = 0; i < nd.length; i++) {
        last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02;
        nd[i] = last * 3.5;
      }
      const noise = ctx.createBufferSource();
      noise.buffer = nb;
      noise.loop = true;
      const nf = ctx.createBiquadFilter();
      nf.type = "lowpass";
      nf.frequency.value = 110;
      const nGain = ctx.createGain();
      nGain.gain.value = 0.06;
      noise.connect(nf).connect(nGain).connect(subMix);
      noise.start();

      // Infrasonic trim: everything under ~22 Hz is inaudible, eats headroom, and makes woofers work for
      // nothing. Cutting it is why this can sit loud enough to feel without ever sounding loud.
      const dc = ctx.createBiquadFilter();
      dc.type = "highpass";
      dc.frequency.value = 22;
      dc.Q.value = 0.5;

      subMix.connect(tone).connect(swell).connect(dc).connect(out).connect(this.master);
      v = { out };
      this.subs.set(name, v);
    }
    const fade = opts.fade ?? 3.0;               // long, so it arrives without announcing itself
    v.out.gain.setTargetAtTime(on ? opts.gain ?? 0.16 : 0.0001, now, fade);
  }

  private installGestureUnlock(): void {
    if (this.unlockInstalled) return;
    this.unlockInstalled = true;
    const unlock = (): void => {
      this.resume();
      if (this.ctx && this.ctx.state === "running") {
        window.removeEventListener("pointerdown", unlock);
        window.removeEventListener("keydown", unlock);
        window.removeEventListener("click", unlock);
      }
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("click", unlock);
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master) this.master.gain.value = this.muted ? 0 : this.volume * MASTER_MAX;
    try {
      localStorage.setItem("lfsfxmute", this.muted ? "1" : "0");
    } catch {
      /* ignore */
    }
    return this.muted;
  }

  get state(): string {
    return this.ctx ? this.ctx.state : "no-ctx";
  }
}

export const sfx = new Sfx();
